import { createSlice } from '@reduxjs/toolkit';

/**
 * Thinking step types for progress indicators
 */
export const THINKING_STEPS = {
  DOCUMENT_SEARCH: 'document_search',
  WEB_SEARCH: 'web_search',
  FETCH_URL: 'fetch_url',
  ANALYZING: 'analyzing',
  WRITING: 'writing',
};

/**
 * Labels for thinking steps
 */
export const THINKING_STEP_LABELS = {
  [THINKING_STEPS.DOCUMENT_SEARCH]: 'Searching documents...',
  [THINKING_STEPS.WEB_SEARCH]: 'Searching the web...',
  [THINKING_STEPS.FETCH_URL]: 'Fetching URL...',
  [THINKING_STEPS.ANALYZING]: 'Analyzing information...',
  [THINKING_STEPS.WRITING]: 'Writing response...',
};

const initialState = {
  // Current active conversation
  currentConversationUuid: null,

  // When true, show blank new conversation (prevents auto-load of last conversation)
  newChatMode: true,

  // Messages for the current conversation (local optimistic updates)
  messages: [],

  // Loading states
  isLoading: false,
  isSending: false,

  // Thinking steps for progress display
  thinkingSteps: [],
  currentThinkingStep: null,

  // Pending questions from AI
  pendingQuestions: null,
  questionAnswers: {},
  isAnsweringQuestion: false,

  // Citation panel state
  selectedCitation: null,
  isCitationPanelOpen: false,

  // Error state
  error: null,

  // The assistant turn in flight, { conversationUuid, messageUuid } or null.
  // Kept in the store on purpose: it survives SPA navigation, so coming back
  // to the conversation resumes polling, and it is reset on identity change.
  activeTurn: null,
};

/**
 * AI slice for managing chat state and progress indicators
 */
const aiSlice = createSlice({
  name: 'ai',
  initialState,
  reducers: {
    // Set the current conversation
    setCurrentConversation: (state, action) => {
      state.currentConversationUuid = action.payload;
      state.newChatMode = false;
      state.messages = [];
      state.thinkingSteps = [];
      state.currentThinkingStep = null;
      state.error = null;
      // Opening another conversation means nothing is in flight for this view.
      // A stranded isSending would block the refill of the messages just cleared.
      state.isSending = false;
      // The turn keeps running on the server; it is re-adopted on return.
      state.activeTurn = null;
      // The question card belongs to the conversation that asked it.
      state.pendingQuestions = null;
      state.questionAnswers = {};
    },

    // Clear current conversation
    clearCurrentConversation: (state) => {
      state.currentConversationUuid = null;
      state.messages = [];
      state.thinkingSteps = [];
      state.currentThinkingStep = null;
      state.error = null;
    },

    // Start a new blank conversation (user explicitly requested, prevents auto-load of last)
    startNewConversation: (state) => {
      state.currentConversationUuid = null;
      state.newChatMode = true;
      state.messages = [];
      state.thinkingSteps = [];
      state.currentThinkingStep = null;
      state.error = null;
      state.pendingQuestions = null;
      state.questionAnswers = {};
      state.isSending = false;
      state.activeTurn = null;
    },

    // Mark a turn as in flight for this view. Sets isSending so the composer
    // locks and the timeline shows; the terminal handlers release it.
    setActiveTurn: (state, action) => {
      state.activeTurn = action.payload;
      state.isSending = true;
    },

    clearActiveTurn: (state) => {
      state.activeTurn = null;
    },

    // Set messages from API
    setMessages: (state, action) => {
      state.messages = action.payload;
    },

    // Add optimistic message (user message before API response)
    addOptimisticMessage: (state, action) => {
      // Whatever the user sends answers the open questions; the server records
      // it that way. The history must not keep showing the options while the
      // reply runs (the message list is only refetched once the turn settles).
      state.messages.forEach((message) => {
        if (message.pending_questions?.length) message.pending_questions = [];
      });
      state.messages.push({
        uuid: `temp-${Date.now()}`,
        role: 'user',
        content: action.payload,
        created_at: new Date().toISOString(),
        isOptimistic: true,
      });
      state.isSending = true;
    },

    // Add assistant message placeholder while loading
    addAssistantPlaceholder: (state) => {
      state.messages.push({
        uuid: `assistant-temp-${Date.now()}`,
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
        isLoading: true,
      });
    },

    // Remove assistant placeholder on error
    removeAssistantPlaceholder: (state) => {
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.isLoading) {
        state.messages.pop();
      }
      state.isSending = false;
      state.thinkingSteps = [];
      state.currentThinkingStep = null;
    },

    // Update assistant message with response
    updateAssistantMessage: (state, action) => {
      const { content, citations, toolCalls, thinkingSteps, generatedFiles } = action.payload;
      const lastMessage = state.messages[state.messages.length - 1];

      if (lastMessage && lastMessage.role === 'assistant') {
        lastMessage.content = content;
        lastMessage.citations = citations || [];
        lastMessage.tool_calls = toolCalls || [];
        lastMessage.thinking_steps = thinkingSteps || [];
        lastMessage.generated_files = generatedFiles || [];
        lastMessage.isLoading = false;
      }

      state.isSending = false;
      state.thinkingSteps = [];
      state.currentThinkingStep = null;
    },

    // Set loading state
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    // Set sending state
    setSending: (state, action) => {
      state.isSending = action.payload;
    },

    // Update thinking steps from polling
    setThinkingSteps: (state, action) => {
      state.thinkingSteps = action.payload;
      if (action.payload.length > 0) {
        state.currentThinkingStep = action.payload[action.payload.length - 1];
      }
    },

    // Add a thinking step ({ tool, args })
    addThinkingStep: (state, action) => {
      state.thinkingSteps.push(action.payload);
      state.currentThinkingStep = action.payload.tool;
    },

    // Clear thinking steps
    clearThinkingSteps: (state) => {
      state.thinkingSteps = [];
      state.currentThinkingStep = null;
    },

    // Open citation panel with selected citation
    openCitationPanel: (state, action) => {
      state.selectedCitation = action.payload;
      state.isCitationPanelOpen = true;
    },

    // Close citation panel
    closeCitationPanel: (state) => {
      state.selectedCitation = null;
      state.isCitationPanelOpen = false;
    },

    // Set error state
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isSending = false;
    },

    // Clear error state
    clearError: (state) => {
      state.error = null;
    },

    // Set pending questions from backend
    setPendingQuestions: (state, action) => {
      const { messageUuid, questions } = action.payload;
      state.pendingQuestions = {
        messageUuid,
        questions,
        currentIndex: 0,
      };
      state.questionAnswers = {};
    },

    // Answer a question (single or multi-select)
    answerQuestion: (state, action) => {
      const { questionId, selectedOptions, otherText } = action.payload;
      state.questionAnswers[questionId] = {
        selectedOptions,
        otherText,
      };
    },

    // Move to next question in wizard
    nextQuestion: (state) => {
      if (state.pendingQuestions) {
        state.pendingQuestions.currentIndex += 1;
      }
    },

    // Set answering state
    setAnsweringQuestion: (state, action) => {
      state.isAnsweringQuestion = action.payload;
    },

    // Clear pending questions after submission
    clearPendingQuestions: (state) => {
      state.pendingQuestions = null;
      state.questionAnswers = {};
      state.isAnsweringQuestion = false;
    },
  },
});

export const {
  setCurrentConversation,
  clearCurrentConversation,
  startNewConversation,
  setMessages,
  addOptimisticMessage,
  addAssistantPlaceholder,
  removeAssistantPlaceholder,
  updateAssistantMessage,
  setLoading,
  setSending,
  setThinkingSteps,
  setActiveTurn,
  clearActiveTurn,
  addThinkingStep,
  clearThinkingSteps,
  openCitationPanel,
  closeCitationPanel,
  setError,
  clearError,
  setPendingQuestions,
  answerQuestion,
  nextQuestion,
  setAnsweringQuestion,
  clearPendingQuestions,
} = aiSlice.actions;

export default aiSlice.reducer;

// Selectors
export const selectCurrentConversationUuid = (state) => state.ai.currentConversationUuid;
export const selectNewChatMode = (state) => state.ai.newChatMode;
export const selectMessages = (state) => state.ai.messages;
export const selectIsLoading = (state) => state.ai.isLoading;
export const selectIsSending = (state) => state.ai.isSending;
export const selectThinkingSteps = (state) => state.ai.thinkingSteps;
export const selectCurrentThinkingStep = (state) => state.ai.currentThinkingStep;
export const selectSelectedCitation = (state) => state.ai.selectedCitation;
export const selectIsCitationPanelOpen = (state) => state.ai.isCitationPanelOpen;
export const selectError = (state) => state.ai.error;
export const selectActiveTurn = (state) => state.ai.activeTurn;

// Pending questions selectors
export const selectPendingQuestions = (state) => state.ai.pendingQuestions;
export const selectQuestionAnswers = (state) => state.ai.questionAnswers;
export const selectIsAnsweringQuestion = (state) => state.ai.isAnsweringQuestion;
export const selectHasPendingQuestions = (state) => state.ai.pendingQuestions !== null;
export const selectCurrentQuestion = (state) => {
  const pending = state.ai.pendingQuestions;
  if (!pending || pending.currentIndex >= pending.questions.length) {
    return null;
  }
  return pending.questions[pending.currentIndex];
};
